/**
 * DeliveryFunnelDomainsModal — per-domain drill-down modal triggered by the
 * "which N domains here →" link in `DeliveryFunnelSection`'s matched-URLs strip
 * (bd deepcontext-be0.6).
 *
 * Purely presentational — no fetch, no mutation. Receives the `domains` array
 * (already on the be0.2 payload) and renders a sortable, searchable table.
 * Default sort: `spent DESC`. Client-side pagination kicks in above 500 rows.
 *
 * The "Matched URLs (count)" column has no per-domain source data on the
 * delivery-funnel payload, so it renders an em-dash per the bead's hard rule
 * ("If a row's per-domain data is unavailable for some columns … render '—',
 * not zero").
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {XMarkIcon, MagnifyingGlassIcon} from '@heroicons/react/24/outline';
import type {DeliveryFunnelDomainRow} from '../types/deliveryFunnel';
import {formatCurrency, formatInteger} from '../utils/format';
import {formatCompactNumber, formatFullNumber} from '../utils/formatCompactNumber';
import {useBackdropDismiss} from '../hooks/useBackdropDismiss';
import {Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell} from './ui/table';

export interface DeliveryFunnelDomainsModalProps {
    open: boolean;
    onClose: () => void;
    domains: DeliveryFunnelDomainRow[];
    /** Pre-formatted date-range label, e.g. "last 7 days". */
    dateRangeLabel: string;
    /** Pre-formatted campaign label, e.g. "campaign 12345" or "all campaigns". */
    campaignLabel: string;
}

type SortKey = 'domain' | 'bidRequests' | 'bids' | 'impressions' | 'spent';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 100;
const PAGINATION_THRESHOLD = 500;

interface ColumnDef {
    key: SortKey;
    label: string;
    testid: string;
    align: 'left' | 'right';
    /** True for the "Matched URLs" placeholder column which has no source data. */
    placeholder?: boolean;
}

const COLUMNS: Array<ColumnDef | { key: 'matchedUrls'; label: string; testid: string; align: 'right'; placeholder: true }> = [
    {key: 'domain', label: 'Domain', testid: 'dfd-col-domain', align: 'left'},
    // The "Matched URLs" column has no per-domain source data in be0.2's
    // payload — render em-dash. Not sortable.
    {key: 'matchedUrls', label: 'Matched URLs', testid: 'dfd-col-matched', align: 'right', placeholder: true},
    {key: 'bidRequests', label: 'Bid requests', testid: 'dfd-col-bidrequests', align: 'right'},
    {key: 'bids', label: 'Bids placed', testid: 'dfd-col-bids', align: 'right'},
    {key: 'impressions', label: 'Impressions', testid: 'dfd-col-impressions', align: 'right'},
    {key: 'spent', label: 'Spent', testid: 'dfd-col-spent', align: 'right'},
];

function compareRows(a: DeliveryFunnelDomainRow, b: DeliveryFunnelDomainRow, key: SortKey, dir: SortDir): number {
    let cmp = 0;
    if (key === 'domain') {
        cmp = a.domain.localeCompare(b.domain);
    } else {
        cmp = (a[key] as number) - (b[key] as number);
    }
    return dir === 'asc' ? cmp : -cmp;
}

const DeliveryFunnelDomainsModal: React.FC<DeliveryFunnelDomainsModalProps> = ({
    open,
    onClose,
    domains,
    dateRangeLabel,
    campaignLabel,
}) => {
    const [sortKey, setSortKey] = useState<SortKey>('spent');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    // Reset transient state every time the modal opens. Without this, a user
    // who reopens the modal would see whatever sort/search/page they left off
    // with — surprising and not in scope here.
    useEffect(() => {
        if (open) {
            setSortKey('spent');
            setSortDir('desc');
            setSearch('');
            setPage(1);
        }
    }, [open]);

    // Escape closes the modal (matches the project's other modal-keyboard idiom).
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return domains;
        return domains.filter((d) => d.domain.toLowerCase().includes(q));
    }, [domains, search]);

    const sorted = useMemo(() => {
        const copy = filtered.slice();
        copy.sort((a, b) => compareRows(a, b, sortKey, sortDir));
        return copy;
    }, [filtered, sortKey, sortDir]);

    const paginated = useMemo(() => {
        if (sorted.length <= PAGINATION_THRESHOLD) return sorted;
        const start = (page - 1) * PAGE_SIZE;
        return sorted.slice(start, start + PAGE_SIZE);
    }, [sorted, page]);

    const showPagination = sorted.length > PAGINATION_THRESHOLD;
    const totalPages = showPagination ? Math.max(1, Math.ceil(sorted.length / PAGE_SIZE)) : 1;

    // Clamp page when search shrinks the result set below current page.
    useEffect(() => {
        if (page > totalPages) setPage(1);
    }, [page, totalPages]);

    const handleSortClick = useCallback(
        (key: SortKey) => {
            setPage(1);
            if (sortKey === key) {
                setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
            } else {
                setSortKey(key);
                // First click on a numeric column defaults to descending, on
                // the domain column defaults to ascending — matches user
                // expectation that "click for biggest" works on metrics.
                setSortDir(key === 'domain' ? 'asc' : 'desc');
            }
        },
        [sortKey],
    );

    const backdropDismiss = useBackdropDismiss(onClose);

    if (!open) return null;

    const headerLine = `Showing ${formatInteger(domains.length)} ${
        domains.length === 1 ? 'domain' : 'domains'
    } where your matched URLs for this blueprint live · ${dateRangeLabel} · ${campaignLabel}`;

    return (
        <div
            data-testid="dfd-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dfd-modal-title"
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            {...backdropDismiss}
        >
            <div className="bg-surface rounded-lg w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
                    <div className="min-w-0">
                        <h2
                            id="dfd-modal-title"
                            className="text-base font-semibold text-ink"
                        >
                            Domains for matched URLs
                        </h2>
                        <p
                            data-testid="dfd-header-line"
                            className="mt-1 text-xs text-ink-muted"
                        >
                            {headerLine}
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="dfd-close"
                        aria-label="Close"
                        onClick={onClose}
                        className="rounded-md p-1 text-ink-muted hover:bg-ghost-bg hover:text-ink"
                    >
                        <XMarkIcon className="h-5 w-5" aria-hidden />
                    </button>
                </div>

                {/* Search */}
                <div className="border-b border-line px-5 py-3">
                    <div className="relative">
                        <MagnifyingGlassIcon
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted"
                            aria-hidden
                        />
                        <input
                            type="text"
                            data-testid="dfd-search"
                            placeholder="Filter by domain…"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            className="w-full rounded-md border border-line bg-surface pl-9 pr-3 py-2 text-sm text-ink focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="flex-1 overflow-auto">
                    <Table data-testid="dfd-table">
                        <TableHead className="sticky top-0 bg-ghost-bg">
                            <tr>
                                {COLUMNS.map((col) => {
                                    const isPlaceholder =
                                        'placeholder' in col && col.placeholder === true;
                                    const isActive =
                                        !isPlaceholder && (col.key as SortKey) === sortKey;
                                    const indicator = isActive
                                        ? sortDir === 'asc'
                                            ? ' ▲'
                                            : ' ▼'
                                        : '';
                                    const alignClass =
                                        col.align === 'right' ? 'text-right' : 'text-left';
                                    return (
                                        <TableHeaderCell
                                            key={col.testid}
                                            data-testid={col.testid}
                                            scope="col"
                                            className={alignClass}
                                        >
                                            {isPlaceholder ? (
                                                <span>{col.label}</span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    data-testid={`${col.testid}-sort`}
                                                    onClick={() => handleSortClick(col.key as SortKey)}
                                                    className={`${alignClass} inline-flex w-full items-center ${
                                                        col.align === 'right'
                                                            ? 'justify-end'
                                                            : 'justify-start'
                                                    } gap-1 hover:text-ink`}
                                                    aria-sort={
                                                        isActive
                                                            ? sortDir === 'asc'
                                                                ? 'ascending'
                                                                : 'descending'
                                                            : 'none'
                                                    }
                                                >
                                                    <span>
                                                        {col.label}
                                                        {indicator}
                                                    </span>
                                                </button>
                                            )}
                                        </TableHeaderCell>
                                    );
                                })}
                            </tr>
                        </TableHead>
                        <TableBody>
                            {paginated.length === 0 ? (
                                <tr data-testid="dfd-empty">
                                    <td
                                        colSpan={COLUMNS.length}
                                        className="px-4 py-8 text-center text-sm text-ink-muted"
                                    >
                                        No domains match the current filter.
                                    </td>
                                </tr>
                            ) : (
                                paginated.map((row) => (
                                    <TableRow
                                        key={row.domain}
                                        data-testid="dfd-row"
                                        data-domain={row.domain}
                                    >
                                        <TableCell className="font-medium">
                                            {row.domain}
                                        </TableCell>
                                        <TableCell
                                            className="text-right text-ink-muted"
                                            data-testid="dfd-cell-matched"
                                        >
                                            —
                                        </TableCell>
                                        <TableCell
                                            className="text-right tabular-nums"
                                            data-testid="dfd-cell-bidrequests"
                                            title={formatFullNumber(row.bidRequests)}
                                        >
                                            {formatCompactNumber(row.bidRequests)}
                                        </TableCell>
                                        <TableCell
                                            className="text-right tabular-nums"
                                            data-testid="dfd-cell-bids"
                                            title={formatFullNumber(row.bids)}
                                        >
                                            {formatCompactNumber(row.bids)}
                                        </TableCell>
                                        <TableCell
                                            className="text-right tabular-nums"
                                            data-testid="dfd-cell-impressions"
                                            title={formatFullNumber(row.impressions)}
                                        >
                                            {formatCompactNumber(row.impressions)}
                                        </TableCell>
                                        <TableCell
                                            className="text-right tabular-nums"
                                            data-testid="dfd-cell-spent"
                                        >
                                            {formatCurrency(row.spent, 'USD')}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Pagination — only when total > 500 (per bead spec) */}
                {showPagination && (
                    <div
                        data-testid="dfd-pagination"
                        className="flex items-center justify-between border-t border-line px-5 py-3 text-xs text-ink-muted"
                    >
                        <span>
                            Page {page} of {totalPages} · {formatInteger(sorted.length)} domains
                        </span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                data-testid="dfd-page-prev"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="rounded-md border border-line px-3 py-1 hover:bg-ghost-bg disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Prev
                            </button>
                            <button
                                type="button"
                                data-testid="dfd-page-next"
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                className="rounded-md border border-line px-3 py-1 hover:bg-ghost-bg disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}

                {/* Footnote — repeated from the parent funnel (cross-advertiser
                    supply caveat applies to the bid-requests column). */}
                <p
                    data-testid="dfd-footnote"
                    className="border-t border-line bg-ghost-bg px-5 py-3 text-sm italic leading-relaxed text-ink-muted"
                >
                    ⓘ The &ldquo;bid requests&rdquo; column counts inventory offered by
                    exchanges on these domains across <b>all</b> advertisers — not just
                    this campaign. It&apos;s a supply-availability number for context, not
                    the campaign&apos;s exclusive slice. Every other column is
                    campaign-specific.
                </p>
            </div>
        </div>
    );
};

export default DeliveryFunnelDomainsModal;
